import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../context/CompanyContext';
import api from '../../services/api';
import './MonthlyAnalysis.css';

const formatCurrency = (n) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(n ?? 0);

const formatMonth = (ym) => {
  if (!ym) return '—';
  const [y, m] = ym.split('-');
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${months[parseInt(m, 10) - 1]} ${y}`;
};

const STATUS_LABELS = {
  pending_approval: 'Pendiente',
  approved: 'Aprobado',
  rejected: 'Rechazado'
};

const MonthlyAnalysis = () => {
  const { companyId } = useCompany();
  const queryClient = useQueryClient();
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());

  const { data, isLoading, error } = useQuery({
    queryKey: ['monthlyAnalysis', companyId, filterYear],
    queryFn: async () => {
      const response = await api.get(
        `/companies/${companyId}/monthly-analysis?year=${filterYear}`
      );
      return response.data.data;
    },
    enabled: !!companyId
  });

  const approveMutation = useMutation({
    mutationFn: async (analysisId) => {
      const response = await api.post(
        `/companies/${companyId}/monthly-analysis/${analysisId}/approve`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['monthlyAnalysis', companyId]);
    }
  });

  const rejectMutation = useMutation({
    mutationFn: async (analysisId) => {
      const response = await api.post(
        `/companies/${companyId}/monthly-analysis/${analysisId}/reject`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['monthlyAnalysis', companyId]);
    }
  });

  const analyses = Array.isArray(data) ? data : [];

  if (isLoading) return <div className="monthly-loading">Cargando análisis mensuales...</div>;
  if (error) {
    return (
      <div className="monthly-error">
        <p>Error al cargar análisis mensuales.</p>
        <p className="error-detail">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="monthly-analysis-page">
      <div className="page-header">
        <h2>Análisis Mensual</h2>
        <p className="page-subtitle">
          Revisa y aprueba o rechaza el análisis mensual. Al aprobar, la parte para cuenta personal se registra.
        </p>
      </div>

      <div className="filters-section">
        <label>
          Año:
          <select
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            className="filter-select"
          >
            {[2024, 2025, 2026, 2027].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="analyses-list">
        {analyses.length === 0 ? (
          <div className="empty-state">
            <p>No hay análisis mensuales para este período.</p>
            <p className="hint">
              Los análisis se crean al recibir datos desde Frankenstein, Javiland u otras fuentes vía API.
            </p>
          </div>
        ) : (
          <table className="analyses-table">
            <thead>
              <tr>
                <th>Mes</th>
                <th>Ingresos</th>
                <th>Egresos</th>
                <th>Utilidad</th>
                <th>Para cuenta personal</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {analyses.map((a) => (
                <tr key={a.id}>
                  <td><strong>{formatMonth(a.month)}</strong></td>
                  <td>{formatCurrency(a.totalIncome)}</td>
                  <td>{formatCurrency(a.totalExpenses)}</td>
                  <td>{formatCurrency(a.profit)}</td>
                  <td>{formatCurrency(a.amountToPersonal)}</td>
                  <td>
                    <span className={`status-badge status-${a.status}`}>
                      {STATUS_LABELS[a.status] || a.status}
                    </span>
                  </td>
                  <td>
                    {a.status === 'pending_approval' && (
                      <div className="action-buttons">
                        <button
                          className="btn-approve"
                          onClick={() => {
                            if (window.confirm('¿Aprobar este análisis? Se registrará el monto en la cuenta personal.')) {
                              approveMutation.mutate(a.id);
                            }
                          }}
                          disabled={approveMutation.isLoading}
                        >
                          Aprobar
                        </button>
                        <button
                          className="btn-reject"
                          onClick={() => {
                            if (window.confirm('¿Rechazar este análisis?')) {
                              rejectMutation.mutate(a.id);
                            }
                          }}
                          disabled={rejectMutation.isLoading}
                        >
                          Rechazar
                        </button>
                      </div>
                    )}
                    {a.status === 'approved' && a.approvedBy && (
                      <span className="approved-by">Aprobado por {a.approvedBy}</span>
                    )}
                    {a.status === 'rejected' && a.rejectedBy && (
                      <span className="rejected-by">Rechazado por {a.rejectedBy}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MonthlyAnalysis;
